import { useApolloClient } from '@apollo/client';
import { IManagedColumn } from '@starryui10/grids/buttonBar/ManageColumnButton';
import { IFooterComponentProps } from '@starryui10/grids/ServerPagedGrid/Footer';
import { IServerPagedGridRefs, ServerPagedGrid } from '@starryui10/grids/ServerPagedGrid/ServerPagedGrid';
import { ServerGridContextProvider } from '@starryui10/grids/ServerPagedGrid/ServerPageGridContext';
import { useSRWGlobalEvent } from '@starryui10/hooks/useSRWGlobalEvent';
import { ColDef } from 'ag-grid-community';
import { cloneDeep } from 'lodash';
import React, { createRef, forwardRef, useEffect, useRef, useState } from 'react';
import { entryDirectoryCurrentPage, entryDirectoryPageAmount, entryDirectorySearchFor } from '../../cache';
import { DirectoryFooter } from '../../components/DirectoryFooter';
import { useMainDirectoryCache } from '../../graphql/useMainDirectorySelectedQuery';
import { useUpdateMainDirectoryClearSelectedItems } from '../../graphql/useUpdateMainDirectoryClearSelectedItems';
import { IGridFormat } from '../../graphql/useUpdateMainDirectoryWorkspace';
import { useDirectoryDispatch, useDirectoryState } from '../../state/DirectoryState';
import { columnFormat } from '../../state/Observables';
import { convertGridToGraphQL } from '../../state/stateUtils';
import { NoDataOverlay } from '@starryui10/overlays/NoDataOverlay';
import { useColumnsProviderContext } from './ColumnsProvider';

interface IGridProps {
    height: number;
}

export interface IGridRefs {
    toggleColumn: (col: IManagedColumn) => void;
}

export const Grid = forwardRef<IGridRefs, IGridProps>(({ height }, ref) => {
    const loaded = useRef(true);

    const buildColumns = (layout: IGridFormat[]): ColDef[] => {
        const sortedColumns: ColDef[] = [];
        const availColumns = cloneDeep(columns?.toArray());

        if (!availColumns) return [];

        layout
            .sort((e) => e.columnOrder)
            .forEach((element) => {
                const index = availColumns.findIndex((e: ColDef) => e.field === element.id || e.colId === element.id);
                const item = availColumns.splice(index, 1)[0];

                item.hide = !element.columnVisibility;
                item.width = element.columnWidth;

                sortedColumns.push(item);
            });

        return [...sortedColumns, ...availColumns];
    };

    const { data, gridFormat, noResultsMessage } = useDirectoryState();
    const gridRef = createRef<IServerPagedGridRefs>();
    const { columns } = useColumnsProviderContext();
    const { dispatch } = useDirectoryDispatch();
    const [cols] = useState(buildColumns(columnFormat()));

    const { cache } = useApolloClient();
    const [mutateClearSelection] = useUpdateMainDirectoryClearSelectedItems();
    const { clearSelectedFromCache } = useMainDirectoryCache(cache);

    React.useImperativeHandle(ref, () => ({
        toggleColumn: (col) => {
            const item = gridRef.current
                ?.columnApi()
                ?.getAllColumns()
                ?.find((e) => e.getColDef().field === col.field);

            if (item) {
                gridRef.current?.columnApi()?.setColumnVisible(item, col.visible);
            }
        },
    }));

    useEffect(() => {
        if (gridRef?.current) {
            if (!loaded.current && gridFormat) {
                gridRef?.current?.gridApi()?.setColumnDefs([]);
                gridRef?.current?.gridApi()?.setColumnDefs(buildColumns(gridFormat));
                gridRef?.current?.autoWidth();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridFormat, gridRef]);

    useEffect(() => {
        if (noResultsMessage) {
            gridRef.current?.gridApi()?.hideOverlay();
            gridRef.current?.gridApi()?.showNoRowsOverlay();
        }
    }, [noResultsMessage, gridRef]);

    useEffect(() => {
        loaded.current = false;
    }, []);

    useSRWGlobalEvent((_, event) => {
        if (event.Success && (event.dbObjectName.startsWith('Entry') || event.dbObjectName === 'SavedList')) {
            if (event.ID) {
                dispatch({ type: 'RELOAD' });
            } else if (event.Key.endsWith('UpdateSituationResponse')) {
                Promise.resolve(event).then(() => {
                    if (event.IDs) {
                        dispatch({ type: 'RELOAD' });
                    }
                });
            }
        }
    }, 'EntryDirectory');

    const handlePaginationClick = (skip: number, limit: number) => {
        dispatch({ type: 'LOAD_PAGE', skip, limit });
        return Promise.resolve([]);
    };

    const handleClearSelection = () => {
        dispatch({ type: 'CLEAR_SELECTED' });
        mutateClearSelection();
        clearSelectedFromCache();
    };

    const handleShowSelectedChange = (value: boolean) => {
        dispatch({ type: 'SHOW_SELECTED', showSelected: value });
    };

    const handleColumnInteraction = () => {
        // TODO this might be not needed until we save.
        const columnState = gridRef.current?.columnApi()?.getColumnState();

        if (columnState) {
            columnFormat(convertGridToGraphQL(columnState) as IGridFormat[]);
        }
    };

    return (
        <div style={{ height: '100%' }}>
            <ServerGridContextProvider
                value={{
                    currentPageCache: entryDirectoryCurrentPage,
                    pageAmount: entryDirectoryPageAmount,
                    getCurrentHighlight: entryDirectorySearchFor,
                    totalRecords: data?.allMainDirectory?.totalCount,
                    noResultsMessage: noResultsMessage,
                }}
            >
                <ServerPagedGrid
                    ref={gridRef}
                    stickyTop={`${height}px`}
                    immutableData
                    onMoreDataRequest={handlePaginationClick}
                    columnDefs={cols}
                    rowData={data?.allMainDirectory.nodes}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    onCellClicked={() => {
                        /* empty */
                    }}
                    footerComponent={DirectoryFooter as React.FC<IFooterComponentProps>}
                    footerComponentProps={{
                        onClearSelection: handleClearSelection,
                        onShowSelected: handleShowSelectedChange,
                    }}
                    handleColumnInteraction={handleColumnInteraction}
                    frameworkComponents={{
                        customNoRowsOverlay: NoDataOverlay,
                    }}
                    noRowsOverlayComponent={'customNoRowsOverlay'}
                />
            </ServerGridContextProvider>
        </div>
    );
});
